import { useApplicationModals } from "app/common/hooks";
import MainLayout from "app/layouts/main";
import { Formik, getIn } from "formik";
import * as Yup from "yup";
import { InputLabel } from "app/components/atoms/form";
import { useCreateForm } from "app/integration/common/hooks";
import api from "app/integration/api";
import { useCollector } from "app/integration/common/hooks";
import { FormHeader, FormSummary } from "app/components/atoms/layout";
import { useCallback } from "react";
import Table from "app/components/atoms/table";
import { Button, IconButton } from "app/components/atoms/button";
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppModals from "app/modals";
import clsx from "clsx";
import style from "style/height.module.css";
import {
  MinusCircleIcon,
  PlusCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { ErrorMessage } from "app/components/atoms/form";
import { useToast } from "app/components/atoms/toast/toast";
import { clampReturQty, isPerQtyOrderItem } from "app/common/returCustomer";
import { useBarcodeScanner } from "app/context/BarcodeScannerContext";

const breadcrumbItems = [
  { label: "Scrap", href: "/scrap" },
  { label: "Tambah Scrap", href: "#" },
];

function CreateScrap() {
  let isError = false;
  const navigate = useNavigate();
  const formScrap = useRef(null);
  const { showToast } = useToast();
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (key) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const { openModal, setPayloads } = useApplicationModals();

  const handleSuccess = () => {
    navigate("/scrap");
  };

  const [productVariants] = useCollector({ modelName: "productVariant" });
  const [qrCode, setQrCode] = useState("");
  const scanBarcodeInputRef = useRef(null);
  const scanQueueRef = useRef(Promise.resolve());

  const makeid = (length) => {
    let result = "";
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    return result;
  };

  const { initialValues, validationSchema, onSubmit, setFieldValue } =
    useCreateForm({
      initialValues: {
        notes: "",
        items: [],
      },
      validationSchema: Yup.object().shape({
        items: Yup.array()
          .of(
            Yup.object().shape({
              product_variant_id: Yup.number().required("Variant harus diisi"),
              product_code: Yup.array().of(
                Yup.object().shape({
                  qr_code: Yup.string().required("Barcode harus diisi"),
                  notes: Yup.string().max(255, "Maksimal 255 karakter."),
                })
              ),
              // Baris per qty jumlahnya diketik, jadi batas bawahnya dijaga di
              // sini; batas atas dijaga saat pengetikan (stok varian).
              qty: Yup.number().required("Jumlah harus diisi").min(1, "Jumlah minimal 1"),
            })
          )
          .required(
            "Item harus diisi. Silahkan tambahkan item melalui input barcode."
          )
          .min(1, "Item harus diisi"),
      }),
      onSubmit: async (values) => {
        try {
          for (let index = 0; index < values?.items?.length; index++) {
            let qr_codes = [];
            const item = values?.items[index];
            const productVariant = productVariants.find(
              (variant) => variant.id === item?.product_variant_id
            );
            for (let i = 0; i < item?.product_code?.length; i++) {
              qr_codes.push(item?.product_code[i]?.qr_code);
            }
            console.log("qr_codes", qr_codes);

            const scrap = await api.scrap.store(
              makeid(10),
              item?.product_variant?.product_detail?.name,
              item?.product_variant_id,
              item?.qty,
              values?.notes,
              "scrap " + new Date().toLocaleDateString(),
              "approved",
              qr_codes
            );

            for (let i = 0; i < item?.product_code?.length; i++) {
              await api.productCode.update(item?.product_code[i]?.id, {
                scrap_id: scrap?.data?.id,
                status: "scrap",
                scan_scrap_date: new Date(),
                scrap_reason: item?.product_code[i]?.notes,
              });
            }
          }
          isError = false;
        } catch (error) {
          setPayloads("errorMessage.view", {
            title: "Add Stock Opname Error",
            message: error?.response?.data?.message,
          });
          isError = true;
          openModal("errorMessage.view");
        }
      },
      onSuccess: () => {
        if (!isError) {
          handleSuccess();
        }
      },
    });

  /**
   * Menjalankan pencarian daftar dan memperlakukan 404 sebagai "tidak ada",
   * bukan galat. Endpoint CRUD backend menjawab 404 + "Belum ada data" untuk
   * hasil kosong (`CrudController.getAll`), jadi tanpa ini setiap barcode yang
   * tidak cocok berhenti di modal galat dan jalur cadangan di bawah tidak
   * pernah tercapai.
   */
  const cariDaftar = useCallback(async (panggil) => {
    try {
      return await panggil();
    } catch (err) {
      if (err?.response?.status === 404) return { data: [] };
      throw err;
    }
  }, []);

  /**
   * Mencari varian lewat `product_variants.qr_code` — barcode yang dipakai
   * bersama seluruh unit sebuah varian. Barang per qty tidak punya
   * `product_codes` sama sekali, jadi inilah satu-satunya barcode yang ada
   * pada fisiknya.
   */
  const cariVarianDariBarcode = useCallback(
    async (value) => {
      try {
        const response = await cariDaftar(() =>
          api.productVariant.index({
            qr_code: value?.replace(/"/g, ""),
            limit: 1,
          })
        );
        return response?.data?.[0] ?? null;
      } catch (err) {
        return null;
      }
    },
    [cariDaftar]
  );

  /**
   * Mengambil data unit produk (product code) berdasarkan nilai barcode yang dipindai,
   * memvalidasi status unit di gudang ('warehouse'), atau mendeteksi barang per qty.
   *
   * @param {string} value - Nilai barcode/QR code yang dipindai.
   * @returns {Promise<object|null>} Data unit produk atau objek per_qty jika valid, atau null jika gagal.
   * @sideEffects
   * - Menampilkan modal errorMessage.view jika barcode tidak ditemukan atau terjadi error API.
   */
  const setItemOrder = useCallback(
    async (value) => {
      try {
        // Get product code directly from backend with specific QR code
        const response = await cariDaftar(() =>
          api.productCode.index({
            qr_code: value?.replace(/"/g, ""), // Remove all quotes globally
            limit: 1,
          })
        );

        const productCode = response.data[0];

        // Check if product code exists and is in warehouse status
        if (!productCode || productCode.status !== "warehouse") {
          // Sebelum menyerah, barcode dicoba sebagai barcode VARIAN: barang per
          // qty memakai satu barcode untuk semua unitnya dan tidak pernah punya
          // `product_codes`, jadi tanpa cabang ini barang seperti itu tidak
          // pernah bisa discrap dari layar ini.
          if (!productCode) {
            const variant = await cariVarianDariBarcode(value);
            if (variant) {
              if (isPerQtyOrderItem({ product_variant: variant })) {
                return { per_qty: true, product_variant: variant };
              }
              setPayloads("errorMessage.view", {
                title: "Unit tidak ditemukan",
                message: `Unit dengan barcode ${value} tidak berstatus gudang — mungkin sudah terjual atau sudah discrap.`,
              });
              isError = true;
              openModal("errorMessage.view");
              return null;
            }
          }

          console.warn(
            `Product code not found or not in warehouse for QR code: ${value}`
          );

          setPayloads("errorMessage.view", {
            title: "Scan Error",
            message: productCode
              ? `Product code ${value} is not in warehouse status`
              : `Product code not found for QR code: ${value}`,
          });
          isError = true;
          openModal("errorMessage.view");
          return null;
        }

        const responseVariant = await api.productVariant.index({
          id: productCode?.product_variant_id,
          limit: 1,
        });

        const productVariant = responseVariant.data[0];
        // Check if product variant exists
        if (!productVariant) {
          console.warn(`Product variant not found for QR code: ${value}`);

          setPayloads("errorMessage.view", {
            title: "Tambah Customer Error",
            message: `Product variant not found for QR code: ${value}`,
          });
          isError = true;
          openModal("errorMessage.view");
          return null;
        }
        productCode.product_variant = productVariant;
        return productCode;
      } catch (err) {
        console.error("Error fetching product code:", err);
        setPayloads("errorMessage.view", {
          title: "API Error",
          message:
            err?.response?.data?.message || "Error fetching product code",
        });
        isError = true;
        openModal("errorMessage.view");
        return null;
      }
    },
    [productVariants, setPayloads, openModal, cariDaftar, cariVarianDariBarcode]
  ); // Add dependencies array

  /**
   * Batas atas scrap per qty = stok varian di Gudang Utama (`product_variant.qty`),
   * otoritas stok untuk gudang itu. Dikembalikan APA ADANYA (0 tetap 0): menaikkan
   * batas minimal ke 1 akan membuat barang berstok nol tetap bisa discrap satu
   * unit — persis stok minus yang hendak dicegah batas ini.
   */
  const maxScrapPerQty = (variant) => Math.max(0, Number(variant?.qty ?? 0));

  /** Mengubah jumlah baris scrap per qty, dijepit ke 1..stok. */
  const setQtyPerQty = (item, nilai, setFieldValue, values) => {
    const qty = clampReturQty(nilai, item?.max);
    if (Number(nilai) > Number(item?.max)) {
      showToast(`Stok produk ini hanya ${item?.max}.`, "error");
    }
    setFieldValue(
      "items",
      values.items.map((element) =>
        element === item ? { ...element, qty } : element
      )
    );
  };

  /** Menghapus seluruh baris — dipakai baris per qty yang tak punya unit. */
  const removeItem = (key, values, setFieldValue) => {
    setFieldValue(
      "items",
      values.items.filter((_, index) => index !== key)
    );
  };

  /**
   * Memproses string barcode (dari scanner hardware global maupun input teks manual),
   * mencari data unit/varian terkait, dan menambahkannya ke state Formik items.
   *
   * @param {string} barcodeValue - String barcode yang dipindai atau diketik.
   * @param {object} currentValues - Objek values Formik saat ini.
   * @param {Function} setFieldValueFn - Fungsi setFieldValue Formik.
   * @returns {Promise<void>}
   * @sideEffects
   * - Menampilkan modal error jika barcode tidak valid atau stok habis.
   * - Menambahkan atau menaikkan qty item pada field 'items' Formik.
   * - Memperbarui state expandedRows untuk baris item yang baru ditambahkan.
   */
  const processScannedBarcode = async (barcodeValue, currentValues, setFieldValueFn) => {
    const qrValue = barcodeValue?.trim();
    if (!qrValue) return;

    try {
      const itemOrder = await setItemOrder(qrValue);

      // Barang per qty: tidak ada unit untuk dicatat, jadi tiap scan hanya
      // menambah jumlah baris varian yang sama.
      if (itemOrder?.per_qty) {
        const variant = itemOrder.product_variant;
        if (maxScrapPerQty(variant) < 1) {
          setPayloads("errorMessage.view", {
            title: "Stok habis",
            message: `Stok ${variant?.product_detail?.name ?? "produk ini"} 0 — tidak ada yang bisa discrap.`,
          });
          isError = true;
          openModal("errorMessage.view");
          setQrCode("");
          return;
        }
        const existing = currentValues?.items?.find(
          (item) => item.product_variant_id === variant?.id
        );
        if (existing) {
          const qty = clampReturQty(
            Number(existing.qty ?? 0) + 1,
            maxScrapPerQty(variant)
          );
          if (qty === existing.qty) {
            showToast(
              `Stok ${variant?.product_detail?.name ?? "produk"} hanya ${maxScrapPerQty(variant)}.`,
              "error"
            );
          }
          setFieldValueFn(
            "items",
            currentValues.items.map((item) =>
              item === existing ? { ...item, qty } : item
            )
          );
        } else {
          setFieldValueFn("items", [
            ...currentValues.items,
            {
              product_variant_id: variant?.id,
              product_variant: variant,
              product_code: [],
              qty: 1,
              max: maxScrapPerQty(variant),
              is_unique_barcode: false,
            },
          ]);
        }
        setQrCode("");
        return;
      }

      if (!itemOrder) {
        setQrCode("");
        return;
      }

      const isAlreadyScanned = (currentValues?.items || []).some((item) =>
        item.product_code?.some((code) => code?.qr_code === itemOrder?.qr_code)
      );
      if (isAlreadyScanned) {
        showToast(`Barcode "${qrValue}" sudah ada di dalam daftar!`, "warning");
        setQrCode("");
        return;
      }

      const existingIndex = (currentValues?.items || []).findIndex(
        (item) => item.product_variant_id === itemOrder.product_variant_id
      );
      if (existingIndex > -1) {
        const updatedItems = currentValues.items.map((item, idx) => {
          if (idx === existingIndex) {
            return {
              ...item,
              product_code: [...(item.product_code || []), itemOrder],
              qty: (Number(item.qty) || 0) + 1,
            };
          }
          return item;
        });
        setFieldValueFn("items", updatedItems);
        setExpandedRows((prev) => ({ ...prev, [existingIndex]: true }));
      } else {
        const newIndex = (currentValues?.items || []).length;
        const newVariant = {
          product_variant_id: itemOrder.product_variant_id,
          product_variant: itemOrder.product_variant,
          product_code: [itemOrder],
          qty: 1,
        };
        setFieldValueFn("items", [...(currentValues?.items || []), newVariant]);
        setExpandedRows((prev) => ({
          ...prev,
          [newIndex]: true,
        }));
      }
      setQrCode("");
    } catch (err) {
      console.error("[Scrap Create] Error processing barcode:", err);
      showToast(`Gagal memproses barcode "${qrValue}"`, "error");
    }
  };

  /**
   * Listener global pemindai barcode untuk halaman Tambah Scrap.
   * Menggunakan scanQueueRef untuk mengeksekusi pemindaian secara serial dan mencegah race condition.
   *
   * @param {string} scannedBarcode - Nilai barcode yang telah dibersihkan oleh scanner buffer global.
   * @returns {void}
   * @sideEffects Menjadwalkan pemrosesan barcode ke antrian serial scanQueueRef.
   */
  const handleGlobalBarcodeScan = (scannedBarcode) => {
    if (!scannedBarcode || !formScrap.current) return;
    scanQueueRef.current = scanQueueRef.current
      .then(async () => {
        if (!formScrap.current) return;
        const { values, setFieldValue } = formScrap.current;
        await processScannedBarcode(scannedBarcode, values, setFieldValue);
      })
      .catch((err) => {
        console.error("[Scrap Create] Scan queue error:", err);
      });
  };

  useBarcodeScanner(handleGlobalBarcodeScan, { info: "Scrap" });

  /**
   * Menangani event pengetikan dan penekanan Enter pada input barcode manual.
   * Mengalirkan pemindaian ke antrian serial yang sama untuk konsistensi state.
   *
   * @param {React.KeyboardEvent|React.ChangeEvent} e - Event input atau keyboard.
   * @param {object} formikProps - Objek bantuan Formik { setFieldValue, values }.
   * @returns {void}
   */
  const handleScanBarcodeChange = (e, { setFieldValue, values }) => {
    // Immediately refocus the input after any change
    if (scanBarcodeInputRef.current) {
      scanBarcodeInputRef.current.focus();
    }

    // Only process when Enter key is pressed
    if (e.key === "Enter" && e.target.value?.trim() !== "") {
      const val = e.target.value;
      e.preventDefault();
      scanQueueRef.current = scanQueueRef.current
        .then(async () => {
          if (!formScrap.current) return;
          const currentValues = formScrap.current.values;
          const currentSetFieldValue = formScrap.current.setFieldValue;
          await processScannedBarcode(val, currentValues, currentSetFieldValue);
        })
        .catch((err) => {
          console.error("[Scrap Create] Manual scan queue error:", err);
        });
    }
  };

  const deleteItem = (
    indexProductVariant,
    indexProductCode,
    values,
    setFieldValue
  ) => {
    const newProductStockOpname = [...values.items];
    const productVariant = newProductStockOpname[indexProductVariant];

    // Remove the specific product code
    productVariant.product_code.splice(indexProductCode, 1);
    productVariant.qty -= 1;

    // If there are no more product codes left, remove the product variant
    if (productVariant.product_code.length === 0) {
      newProductStockOpname.splice(indexProductVariant, 1);
    }

    setFieldValue("items", newProductStockOpname);
    setExpandedRows((prev) => ({ ...prev, [indexProductVariant]: false }));
  };

  const handleSuccessProductChoose = (product) => {
    if (formScrap.current) {
      const values = formScrap.current?.values;

      const existingItem = values.items?.find(
        (existingItem) =>
          existingItem.product_variant_id == product?.product_variant_id
      );

      if (existingItem) {
        const isQrCodeExists = existingItem.product_code?.find(
          (item) => item.qr_code === product?.qr_code
        );
        if (!isQrCodeExists) {
          existingItem.product_code = [...existingItem.product_code, product];
          existingItem.qty += 1;
          formScrap.current.setFieldValue("items", [existingItem]);
        }
        setExpandedRows((prev) => ({ ...prev, [existingItem]: true }));
      } else {
        // If product doesn't exist, add new item
        formScrap.current.setFieldValue("items", [
          ...values.items,
          {
            product_variant_id: product?.product_variant_id,
            product_variant: product?.product_variant,
            product_code: [product],
            qty: 1,
          },
        ]);
        setExpandedRows((prev) => ({ ...prev, [values.items.length]: true }));
      }
      showToast("Anda telah menambahkan produk ke dalam scrap", "success");
    }
  };

  return (
    <MainLayout
      activeSidebarNavigation="warehouse"
      activeSidebarChild="scrap"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Tambah Scrap"
      headingButtons={[
        {
          type: "button",
          label: "Scan Barcode", // This will be shown as a tooltip
          onClick: () => {
            setPayloads("product.scanQrProductCode", { status: "warehouse" });
            openModal("product.scanQrProductCode");
          },
        },
      ]}
    >
      <AppModals
        items={["errorMessage.view", "product.scanQrProductCode"]}
        onSuccess={{
          "errorMessage.view": () => fetch(),
          "product.scanQrProductCode": (product) => {
            handleSuccessProductChoose(product);
          },
        }}
      />
      <Formik
        {...{ initialValues, validationSchema, onSubmit }}
        enableReinitialize={true}
        innerRef={formScrap}
      >
        {({
          values,
          errors,
          status,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form
            className={clsx(
              "px-2 overflow-y-auto",
              style["main-content-height"]
            )}
            onSubmit={handleSubmit}
          >
            <FormHeader>
              <div className="">
                <InputLabel>Scan Barcode Disini</InputLabel>
                <input data-testid="scrap-create-scan-barcode-input"
                  data-allow-global-scan="true"
                  className="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-gray-900 placeholder-gray-300 shadow-sm focus:border-primary-600 focus:outline-none disabled:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500 autofill:dark:bg-gray-800 dark:disabled:bg-gray-800 focus:ring-1 focus:ring-primary-600"
                  type="text"
                  name="scan_barcode"
                  onChange={(e) => {
                    setQrCode(e.target.value);
                    handleScanBarcodeChange(e, { setFieldValue, values });
                  }}
                  onKeyDown={(e) => {
                    // Trigger scanning on Enter key
                    if (e.key === "Enter") {
                      e.preventDefault(); // Prevent the default form submission
                      handleScanBarcodeChange(e, { setFieldValue, values });
                    }
                  }}
                  onBlur={(e) => {
                    handleBlur(e);
                    // Immediately refocus if blur occurs
                    if (scanBarcodeInputRef.current) {
                      scanBarcodeInputRef.current.focus();
                      scanBarcodeInputRef.current.select();
                    }
                  }}
                  value={qrCode}
                />
              </div>
            </FormHeader>

            <Table.Container>
              <Table.Wrapper asIndex={false}>
                <Table.Header>
                  <Table.HeaderRow>
                    <Table.Heading>Product Name</Table.Heading>
                    <Table.Heading>Warna</Table.Heading>
                    <Table.Heading>Ukuran</Table.Heading>
                    <Table.Heading>Jumlah</Table.Heading>
                    <Table.Heading>Actions</Table.Heading>
                  </Table.HeaderRow>
                </Table.Header>
                <Table.Body>
                  {values?.items?.map((scrap, key) => {
                    return (
                      <React.Fragment key={key}>
                        <Table.BodyRow key={key}>
                          <Table.Cell>
                            <strong>
                              {scrap?.product_variant?.product_detail?.name}
                            </strong>
                          </Table.Cell>
                          <Table.Cell>
                            {scrap?.product_variant?.color?.name}
                          </Table.Cell>
                          <Table.Cell>
                            {scrap?.product_variant?.size?.name}
                          </Table.Cell>
                          <Table.Cell>
                            {scrap?.is_unique_barcode === false ? (
                              <input
                                data-testid={`scrap-create-qty-input-${scrap?.product_variant_id}`}
                                type="number"
                                min={1}
                                max={scrap?.max}
                                className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                value={scrap?.qty}
                                onChange={(e) =>
                                  setQtyPerQty(scrap, e.target.value, setFieldValue, values)
                                }
                              />
                            ) : (
                              scrap?.qty
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {scrap?.is_unique_barcode === false ? (
                              /* Baris per qty tidak punya unit untuk ditampilkan
                                 satu per satu — jumlahnya diketik, dan
                                 penghapusan membuang seluruh barisnya. */
                              <IconButton testId="scrap-create-remove-item-button"
                                icon={TrashIcon}
                                variant="danger"
                                onClick={() => removeItem(key, values, setFieldValue)}
                              />
                            ) : (
                              scrap?.product_code?.length > 0 && (
                                <IconButton testId="scrap-create-tampilkan-kode-button"
                                  icon={
                                    expandedRows[key]
                                      ? MinusCircleIcon
                                      : PlusCircleIcon
                                  }
                                  onClick={() => toggleRow(key)}
                                  title={
                                    expandedRows[key]
                                      ? "Sembunyikan Kode"
                                      : "Tampilkan Kode"
                                  }
                                  className="text-gray-500 hover:text-blue-500"
                                />
                              )
                            )}
                          </Table.Cell>
                        </Table.BodyRow>
                        {expandedRows[key] &&
                          scrap?.product_code?.map((productCode, index) => (
                            <Table.BodyRow key={index}>
                              <Table.Cell></Table.Cell>
                              <Table.Cell>{productCode?.qr_code}</Table.Cell>
                              <Table.Cell colSpan={2}>
                                <textarea data-testid="scrap-create-tambah-catatan-optional-textarea"
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  rows={2}
                                  maxLength={255}
                                  placeholder="Tambah Catatan (optional)"
                                  value={productCode?.notes || ""}
                                  onChange={(e) =>
                                    setFieldValue(
                                      `items.${key}.product_code.${index}.notes`,
                                      e.target.value
                                    )
                                  }
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <div className="flex items-center justify-center gap-2">
                                  <IconButton testId="scrap-create-trash-button"
                                    onClick={() =>
                                      deleteItem(
                                        key,
                                        index,
                                        values,
                                        setFieldValue
                                      )
                                    }
                                    icon={TrashIcon}
                                    variant="danger"
                                  />
                                </div>
                              </Table.Cell>
                            </Table.BodyRow>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </Table.Body>
              </Table.Wrapper>
            </Table.Container>
            <ErrorMessage>{getIn(errors, "items")}</ErrorMessage>

            <FormSummary className="w-full">
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catatan
                </label>
                <textarea data-testid="scrap-create-notes-textarea"
                  name="notes"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values?.notes || ""}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  rows="3"
                  placeholder="Tulis catatan disini..."
                />
              </div>
            </FormSummary>

            <div className="fixed bottom-2 left-0 sm:left-auto right-0 sm:right-4 sm:bottom-4 sm:w-auto z-40 flex justify-end border-t border-gray-100 bg-white sm:bg-transparent p-4 sm:p-0 dark:border-gray-700 dark:bg-gray-800 print:hidden sm:absolute sm:rounded-b-lg sm:border-0 ">
              <Button testId="scrap-create-simpan-button"
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {isSubmitting ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </MainLayout>
  );
}

export default CreateScrap;
